import * as React from 'react';
import { invariant } from '@/util/invariant';

type ContextType = {
  state: ReadonlyMap<string, string | Record<string, unknown>>;
  clear: () => void;
  select: (
    field_name: string,
    object_id: string,
    language_code?: string | null,
  ) => void;
};

const Context = React.createContext<ContextType | null>(null);

export const SelectedForMergeProvider: React.FC<{
  children: React.ReactNode;
}> = (props) => {
  const [state, setState] = React.useState<ContextType['state']>(
    () => new Map(),
  );

  const value = React.useMemo<ContextType>(() => {
    const select: ContextType['select'] = (
      field_name,
      object_id,
      language_code,
    ) => {
      setState((state) => {
        const newState = new Map(state);

        if (language_code) {
          const value = newState.get(field_name) as
            | Record<string, unknown>
            | undefined;

          newState.set(field_name, { ...value, [language_code]: object_id });
        } else {
          newState.set(field_name, object_id);
        }

        return newState;
      });
    };

    const clear: ContextType['clear'] = () => {
      setState((state) => {
        if (state.size === 0) {
          return state;
        }

        return new Map();
      });
    };

    return { state, clear, select };
  }, [state]);

  return <Context value={value}>{props.children}</Context>;
};

function useSelectedForMergeContext() {
  const context = React.use(Context);

  invariant(
    context,
    'useSelectedForMergeContext must be used within a SelectedForMergeProvider',
  );

  return context;
}

export type Select = ContextType['select'];

export function useSelect() {
  const { select } = useSelectedForMergeContext();

  return select;
}

export function useClear() {
  const { clear } = useSelectedForMergeContext();

  return clear;
}

export function useSelectedForMerge() {
  const { state } = useSelectedForMergeContext();

  return Object.fromEntries(state);
}
